import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveIcon from '@mui/icons-material/Remove';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { roleCenterIcon } from './roleCenterIcons';
import type { Catalog, RoleView } from './roleCenterApi';

/**
 * Macierze centrum ról (decyzja użytkownika 22.10.2026, S-22.10-01) —
 * podgląd całości: rola × uprawnienie i rola × widoczność, z filtrem grup.
 * Zmiany robi się na karcie roli (klik w nagłówek kolumny otwiera kartę).
 * Tabela ma stałe kolumny i ściśnięte nagłówki, żeby dziesięć ról mieściło
 * się przy 1366 px bez przewijania w bok.
 */

const cellSx = { px: 0.5, py: 0.5, textAlign: 'center' as const };

const RoleHeader = ({ role, onOpen }: { role: RoleView; onOpen: (id: string) => void }) => (
  <TableCell sx={{ ...cellSx, verticalAlign: 'bottom' }}>
    <Tooltip title={`${role.name} — otwórz kartę roli`}>
      <Box
        component="button"
        type="button"
        onClick={() => onOpen(role.id)}
        sx={{
          border: 0,
          background: 'none',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.25,
          width: '100%',
          color: 'inherit',
          font: 'inherit',
        }}
        data-testid={`role-matrix-head-${role.id}`}
      >
        {roleCenterIcon(role.icon, { color: 'primary' })}
        <Typography
          variant="caption"
          sx={{ lineHeight: 1.1, wordBreak: 'break-word', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
        >
          {role.name}
        </Typography>
      </Box>
    </Tooltip>
  </TableCell>
);

const GroupFilter = ({
  options,
  value,
  onChange,
  testId,
}: {
  options: Array<{ key: string; name: string }>;
  value: string;
  onChange: (v: string) => void;
  testId: string;
}) => (
  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }} data-testid={testId}>
    {[{ key: 'all', name: 'Wszystkie' }, ...options].map((o) => (
      <Chip
        key={o.key}
        label={o.name}
        color={value === o.key ? 'primary' : 'default'}
        variant={value === o.key ? 'filled' : 'outlined'}
        onClick={() => onChange(o.key)}
        data-testid={`${testId}-${o.key}`}
      />
    ))}
  </Stack>
);

export const PermissionMatrix = ({
  catalog,
  roles,
  onOpen,
}: {
  catalog: Catalog;
  roles: RoleView[];
  onOpen: (id: string) => void;
}) => {
  const [group, setGroup] = useState('all');
  const groups = catalog.permissionGroups.filter((g) => group === 'all' || g.key === group);
  return (
    <Card variant="outlined" data-testid="role-matrix-permissions">
      <CardContent>
        <Stack spacing={2}>
          <GroupFilter options={catalog.permissionGroups} value={group} onChange={setGroup} testId="role-matrix-group" />
          <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '24%' }}>Uprawnienie</TableCell>
                {roles.map((r) => (
                  <RoleHeader key={r.id} role={r} onOpen={onOpen} />
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((g) => [
                <TableRow key={`g-${g.key}`}>
                  <TableCell colSpan={roles.length + 1} sx={{ bgcolor: 'action.hover', py: 0.5 }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      {roleCenterIcon(g.icon)}
                      <Typography variant="subtitle2">{g.name}</Typography>
                    </Stack>
                  </TableCell>
                </TableRow>,
                ...catalog.permissions
                  .filter((p) => p.group === g.key)
                  .map((p) => (
                    <TableRow key={p.key} data-testid={`role-matrix-row-${p.key}`}>
                      <TableCell>
                        <Tooltip title={p.description}>
                          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                            {roleCenterIcon(p.icon, { color: 'action' })}
                            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                              {p.name}
                            </Typography>
                          </Stack>
                        </Tooltip>
                      </TableCell>
                      {roles.map((r) => {
                        const cell = r.permissionCells.find((c) => c.key === p.key);
                        const on = r.permissions.includes(p.key);
                        return (
                          <TableCell key={r.id} sx={cellSx} data-testid={`role-matrix-${r.id}-${p.key}`} data-granted={on ? 'tak' : 'nie'}>
                            <Tooltip title={cell?.locked ? (cell.lockReason ?? '') : on ? 'nadane' : 'brak'}>
                              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                {on ? (
                                  <CheckCircleIcon fontSize="small" color="success" />
                                ) : (
                                  <RemoveIcon fontSize="small" color="disabled" />
                                )}
                                {cell?.locked ? <LockIcon sx={{ fontSize: 12, ml: 0.25 }} color="disabled" /> : null}
                              </Box>
                            </Tooltip>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  )),
              ])}
            </TableBody>
          </Table>
          <Typography variant="caption" color="text.secondary">
            Kłódka — tego nie zmienia się w centrum (administrator ma zawsze wszystko, konto integracji bez zmian, gość
            bez zmian danych). Kliknij nazwę roli, żeby otworzyć jej kartę.
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
};

export const VisibilityMatrix = ({
  catalog,
  roles,
  onOpen,
}: {
  catalog: Catalog;
  roles: RoleView[];
  onOpen: (id: string) => void;
}) => {
  const [area, setArea] = useState('all');
  const areas = catalog.areas.filter((a) => area === 'all' || a.key === area);
  return (
    <Card variant="outlined" data-testid="role-matrix-visibility">
      <CardContent>
        <Stack spacing={2}>
          <GroupFilter options={catalog.areas} value={area} onChange={setArea} testId="role-matrix-area" />
          <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '24%' }}>Element</TableCell>
                {roles.map((r) => (
                  <RoleHeader key={r.id} role={r} onOpen={onOpen} />
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {areas.map((a) => {
                const items = catalog.elements.filter((e) => e.area === a.key);
                if (items.length === 0) return null;
                return [
                  <TableRow key={`a-${a.key}`}>
                    <TableCell colSpan={roles.length + 1} sx={{ bgcolor: 'action.hover', py: 0.5 }}>
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        {roleCenterIcon(a.icon)}
                        <Typography variant="subtitle2">{a.name}</Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>,
                  ...items.map((e) => (
                    <TableRow key={e.key} data-testid={`role-matrix-row-${e.key}`}>
                      <TableCell>
                        <Tooltip title={e.description}>
                          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                            {roleCenterIcon(e.icon, { color: 'action' })}
                            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                              {e.name}
                            </Typography>
                          </Stack>
                        </Tooltip>
                      </TableCell>
                      {roles.map((r) => {
                        const s = r.elements.find((x) => x.key === e.key);
                        const title = s?.visible
                          ? 'widoczny'
                          : s?.hiddenExplicitly && s.permitted
                            ? 'ukryty w centrum'
                            : (s?.lockReason ?? 'niewidoczny');
                        return (
                          <TableCell key={r.id} sx={cellSx} data-testid={`role-matrix-${r.id}-${e.key}`} data-visible={s?.visible ? 'tak' : 'nie'}>
                            <Tooltip title={title}>
                              <Box component="span" sx={{ display: 'inline-flex' }}>
                                {s?.visible ? (
                                  <CheckCircleIcon fontSize="small" color="success" />
                                ) : s?.permitted ? (
                                  <VisibilityOffIcon fontSize="small" color="warning" />
                                ) : (
                                  <RemoveIcon fontSize="small" color="disabled" />
                                )}
                              </Box>
                            </Tooltip>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  )),
                ];
              })}
            </TableBody>
          </Table>
          <Typography variant="caption" color="text.secondary">
            Zielony — widoczny; przekreślone oko — ukryty w centrum mimo uprawnienia; kreska — rola nie ma uprawnienia,
            więc element i tak jest niewidoczny.
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
};
